// components/TanzaniaSafari.jsx
import {
  FaMapMarkerAlt,
  FaRegClock,
  FaPlane,
  FaStar,
  FaBinoculars,
  FaCamera,
  FaUtensils,
  FaBed,
  FaPaw,
  FaGlobeAfrica
} from "react-icons/fa";
import { IoMdArrowDropright, IoMdCheckmarkCircle } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";

import imageHero from "../../assets/kilimanjaro.jpg";
import imageDay1 from "../../assets/kili2.jpg";
import imageDay2 from "../../assets/beans.jpg";
import imageDay3 from "../../assets/banner6.jpg";
import imageDay4 from "../../assets/banner6.jpg";
import imageDay5 from "../../assets/banner6.jpg";
import imageDay6 from "../../assets/banner6.jpg";
import imageDay7 from "../../assets/banner6.jpg";
import imageDay8 from "../../assets/banner6.jpg";

const hero = {
  kicker: "Great Migration Experiences",
  title: "Witness the Wonder",
  subtitle:
    "Follow the Serengeti herds through river crossings, predators, and sweeping plains for the world's greatest wildlife spectacle.",
  backgroundImage: imageHero,
  meta: ["Migration timing focus", "Expert guides", "Private game drives"],
};

const overview =
  "The Great Migration showcases millions of wildebeest, zebras, and gazelles shifting between the Serengeti and Maasai Mara. This itinerary targets crossing windows, predator activity, and dramatic landscapes with handpicked camps located near the action.";

const highlights = [
  {
    icon: FaMapMarkerAlt,
    title: "Prime Migration Zones",
    description: "Stay near Kimondo, Grumeti, and Mara for the most dramatic crossings.",
    color: "#d97706"
  },
  {
    icon: FaBed,
    title: "Luxury Camp Comfort",
    description: "Relax between drives in private tented camps with gourmet dining and wellness touches.",
    color: "#10b981"
  },
  {
    icon: FaCamera,
    title: "Cinematic Wildlife",
    description: "Witness river crossings, predators, and the endless plains from curated viewpoints.",
    color: "#7c3aed"
  },
  {
    icon: FaPlane,
    title: "White-Glove Logistics",
    description: "Private transfers, migration tracking, and flexible itineraries keep the focus on wildlife.",
    color: "#3b82f6"
  },
];

const details = [
  {
    icon: FaGlobeAfrica,
    title: "Location",
    description: "Serengeti National Park and northern grasslands—timed to follow the herds.",
  },
  {
    icon: FaRegClock,
    title: "Best Time",
    description: "June to October for northern crossings; December to March for southern calving.",
  },
  {
    icon: FaBinoculars,
    title: "Wildlife Focus",
    description: "Big Five, Great Migration, predator action, and 500+ bird species.",
  },
  {
    icon: FaBed,
    title: "Accommodation",
    description: "Handpicked luxury tented camps and lodges near migration action.",
  },
];

const itinerary = [
  {
    dayLabel: "Day 1",
    dayNum: "01",
    title: "Arrival & Migration Briefing",
    image: imageDay1,
    body: "Arrive via Kilimanjaro or Arusha, brief with migration specialists, and rest before dawn drives. Your expert guide will review current herd locations, river crossing probabilities, and optimal photography windows based on real-time migration data.",
    activities: "Arrival, briefing, camp orientation",
    tags: ["Arrival", "Expert Briefing", "Camp Setup"]
  },
  {
    dayLabel: "Day 2",
    dayNum: "02",
    title: "Track Herds & River Crossings",
    image: imageDay2,
    body: "Spend the full day shadowing wildebeest herds, crossing the Grumeti river, and capturing predator drama. Our vehicles are positioned strategically based on morning scout reports to maximize your chances of witnessing dramatic crossings and lion/cheetah hunts.",
    activities: "Full-day game drives, photography focus",
    tags: ["River Crossings", "Predator Action", "Migration Tracking"]
  },
  {
    dayLabel: "Day 3",
    dayNum: "03",
    title: "Extended Safari & Departure",
    image: imageDay3,
    body: "Enjoy a sunrise drive for golden-hour wildlife photography, lunch at camp, and transfer back to Arusha or Kilimanjaro with once-in-a-lifetime memories. Your guide will share migration insights and help select the best photos from your journey.",
    activities: "Morning safari, departure transfer",
    tags: ["Sunrise Drive", "Photo Review", "Farewell"]
  },
];

// Full 7-Day Northern Circuit Itinerary (for the detailed section)
const fullItinerary = [
  {
    dayLabel: "Day 1",
    title: "Arrival – Arusha – Tarangire",
    image: imageDay1,
    body: "Upon arrival at Kilimanjaro International Airport or Tanzania–Kenya Border, you will be warmly met by our representative and transferred to Arusha for lunch. In the afternoon, drive to Tarangire National Park for dinner and an overnight stay.",
    activities: "Meals: Lunch, Dinner • Accommodation: Tarangire Lodge",
    tags: ["Arrival", "Tarangire", "Elephants"]
  },
  {
    dayLabel: "Day 2",
    title: "Tarangire Game Viewing → Lake Manyara",
    image: imageDay2,
    body: "Morning game drives in Tarangire—Tanzania's third-largest national park, best known for its massive elephant herds and ancient baobab trees. Observe diverse wildlife magnetized by the Tarangire River. After lunch, board your safari vehicle and drive to Lake Manyara for dinner and overnight.",
    activities: "Meals: Breakfast, Lunch, Dinner • Accommodation: Lake Manyara Lodge",
    tags: ["Elephants", "Baobabs", "Birding"]
  },
  {
    dayLabel: "Day 3",
    title: "Lake Manyara → Serengeti",
    image: imageDay3,
    body: "After breakfast, embark on a game drive in Lake Manyara National Park—famous for tree-climbing lions, huge baboon troops, and flamingo-filled alkaline waters. The park's varied landscape hosts wildebeest, buffalo, hippo, zebra, and 400+ bird species. Afternoon drive to Serengeti National Park for dinner and overnight.",
    activities: "Meals: Breakfast, Lunch, Dinner • Accommodation: Serengeti Camp",
    tags: ["Tree-Climbing Lions", "Flamingos", "Serengeti Arrival"]
  },
  {
    dayLabel: "Day 4",
    title: "Serengeti National Park – Full Day",
    image: imageDay4,
    body: "Full day tracking wildlife in the mighty Serengeti—Tanzania's most renowned park and home to the planet's greatest concentration of large mammals. 'Serengeti' means 'endless plains' in Maasai, and these rolling grasslands provide exceptional game viewing. This is the highlight of your safari: your own 'Out of Africa' experience in total wilderness.",
    activities: "Meals: Breakfast, Lunch, Dinner • Accommodation: Serengeti Camp",
    tags: ["Great Migration", "Big Five", "Endless Plains"]
  },
  {
    dayLabel: "Day 5",
    title: "Serengeti → Ngorongoro via Oldupai Gorge",
    image: imageDay5,
    body: "With picnic lunch, enjoy en route game drives to Ngorongoro Conservation Area via Oldupai Gorge—the 'Cradle of Mankind' where Dr. Louis and Mary Leakey made groundbreaking discoveries on human origins. After an interpretive presentation at the gorge museum, continue to your lodge on the crater rim for dinner and overnight.",
    activities: "Meals: Breakfast, Lunch, Dinner • Accommodation: Ngorongoro Rim Lodge",
    tags: ["Oldupai Gorge", "Human Origins", "Crater Rim"]
  },
  {
    dayLabel: "Day 6",
    title: "Ngorongoro Crater Game Viewing",
    image: imageDay6,
    body: "Rise early and descend into the famous Ngorongoro Crater—a veritable 'Garden of Eden'. This UNESCO World Heritage Site is a natural amphitheater with the world's highest density of carnivores. With short grasslands and concentrated wildlife, game viewing is exceptional: black rhino, lions, elephants, and more, all within one breathtaking caldera.",
    activities: "Meals: Breakfast, Lunch, Dinner • Accommodation: Ngorongoro Rim Lodge",
    tags: ["Black Rhino", "Crater Floor", "Big Five"]
  },
  {
    dayLabel: "Day 7",
    title: "Ngorongoro → Arusha → Departure",
    image: imageDay7,
    body: "After breakfast, drive back to Arusha with an en route visit to the Iraqwi Cultural Center for a traditional tour. After lunch in Arusha, you will be transferred to Kilimanjaro Airport for your international flight, concluding your unforgettable Northern Circuit safari.",
    activities: "Meals: Breakfast, Lunch • End of Tour",
    tags: ["Cultural Visit", "Departure", "Memories"]
  },
];

export default function TanzaniaSafari() {
  
  // Migration/Safari theme: Earth amber, deep green, warm neutrals
  const theme = {
    primary: "#d97706",      // Amber-600 (safari sunset)
    primaryLight: "#f59e0b", // Amber-500
    accent: "#047857",       // Emerald-700 (wildlife/nature)
    bg: "#fffbeb",           // Warm cream background
    cardBg: "#ffffff",
    text: "#1e293b",
    textMuted: "#64748b"
  };

  const appContext = useContext(AppContext);

  return (
    <div style={{ 
      maxWidth: "1400px", 
      margin: "0 auto", 
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      color: theme.text,
      background: theme.bg
    }}>
      
      {/* ================= HERO SECTION ================= */}
      <section
        style={{
          height: "75vh",
          minHeight: "500px",
          backgroundImage: `linear-gradient(135deg, rgba(23, 15, 8, 0.92), rgba(120, 53, 15, 0.82)), url(${hero.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          textAlign: "center",
          padding: "0 1.5rem",
          position: "relative"
        }}
      >
        <div style={{ maxWidth: "800px", animation: "fadeInUp 0.8s ease-out" }}>
          <span 
            className="badge rounded-pill px-4 py-2 mb-4"
            style={{ 
              background: "rgba(245,158,11,0.25)", 
              border: "1px solid rgba(245,158,11,0.5)",
              color: "#fcd34d",
              fontSize: "0.9rem",
              fontWeight: "600",
              letterSpacing: "0.5px",
              backdropFilter: "blur(4px)"
            }}
          >
            {hero.kicker}
          </span>
          
          <h1 
            style={{ 
              fontSize: "clamp(2.5rem, 6vw, 4rem)", 
              marginBottom: "1rem", 
              lineHeight: 1.1,
              fontWeight: "700",
              textShadow: "0 2px 15px rgba(0,0,0,0.5)"
            }}
          >
            {hero.title}
          </h1>
          
          <p 
            style={{ 
              fontSize: "clamp(1.1rem, 2.5vw, 1.35rem)", 
              marginBottom: "2.5rem",
              opacity: 0.95,
              lineHeight: 1.6
            }}
          >
            {hero.subtitle}
          </p>
          
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m, i) => (
              <span 
                key={i} 
                style={{ 
                  background: "rgba(255,255,255,0.18)", 
                  padding: "0.5rem 1.25rem", 
                  borderRadius: "50px",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  border: "1px solid rgba(255,255,255,0.25)",
                  backdropFilter: "blur(4px)"
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div style={{ padding: "4rem 1.5rem" }}>
        
        {/* ================= OVERVIEW ================= */}
        <section style={{ marginBottom: "5rem", textAlign: "center", maxWidth: "800px", margin: "0 auto 5rem" }}>
          <h2 
            style={{ 
              fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
              marginBottom: "1.5rem", 
              color: theme.text,
              fontWeight: "700"
            }}
          >
            The Great Migration Experience
          </h2>
          <p 
            style={{ 
              fontSize: "1.1rem", 
              lineHeight: 1.8, 
              color: theme.textMuted,
              marginBottom: "2rem"
            }}
          >
            {overview}
          </p>
          
          {/* Quick Stats */}
          <div className="row g-3 justify-content-center" style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {details.map((d, i) => {
              const DIcon = d.icon;
              return (
                <div 
                  key={i}
                  className="col-6 col-md-3"
                  style={{
                    background: theme.cardBg,
                    padding: "1rem 1.5rem",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    minWidth: "200px",
                    border: "1px solid rgba(0,0,0,0.04)"
                  }}
                >
                  <DIcon size={22} style={{ color: theme.primary, flexShrink: 0 }} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: "600", color: theme.text }}>{d.title}</div>
                    <div style={{ fontSize: "0.85rem", color: theme.textMuted }}>{d.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ================= HIGHLIGHTS GRID ================= */}
        <section style={{ marginBottom: "5rem" }}>
          <div className="text-center mb-4">
            <h2 
              style={{ 
                fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
                marginBottom: "0.5rem", 
                color: theme.text,
                fontWeight: "700"
              }}
            >
              Migration Safari Highlights
            </h2>
            <p style={{ color: theme.textMuted }}>Curated experiences for the ultimate wildlife spectacle</p>
          </div>
          
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
            gap: "1.5rem" 
          }}>
            {highlights.map((h, i) => {
              const HIcon = h.icon;
              return (
                <article 
                  key={i} 
                  style={{ 
                    background: theme.cardBg,
                    padding: "1.75rem",
                    borderRadius: "16px",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    cursor: "default",
                    border: "1px solid rgba(0,0,0,0.04)",
                    position: "relative",
                    overflow: "hidden"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.12)";
                    e.currentTarget.style.borderColor = h.color + "66";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.08)";
                    e.currentTarget.style.borderColor = "rgba(0,0,0,0.04)";
                  }}
                >
                  {/* Subtle gradient accent */}
                  <div 
                    className="position-absolute top-0 end-0 opacity-10"
                    style={{
                      width: "100px",
                      height: "100px",
                      background: `radial-gradient(circle at top right, ${h.color}33, transparent 70%)`,
                      borderRadius: "0 0 0 100%"
                    }}
                  />
                  
                  <div 
                    className="d-inline-flex align-items-center justify-content-center rounded-2 mb-3 position-relative"
                    style={{
                      width: "52px",
                      height: "52px",
                      background: `linear-gradient(135deg, ${h.color}20, ${h.color}10)`,
                      color: h.color,
                      fontSize: "1.4rem",
                      border: `1px solid ${h.color}40`
                    }}
                  >
                    <HIcon />
                  </div>
                  <h3 style={{ fontSize: "1.25rem", marginBottom: "0.75rem", fontWeight: "600", color: theme.text, position: "relative" }}>
                    {h.title}
                  </h3>
                  <p style={{ color: theme.textMuted, lineHeight: 1.6, fontSize: "0.95rem", margin: 0, position: "relative" }}>
                    {h.description}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        {/* ================= MIGRATION FOCUS SECTION ================= */}
        <section 
          style={{ 
            background: `linear-gradient(135deg, ${theme.primary}08, ${theme.accent}08)`,
            borderRadius: "20px",
            padding: "2.5rem 2rem",
            marginBottom: "4rem",
            border: `1px solid ${theme.primary}20`
          }}
        >
          <div className="text-center mb-4">
            <FaPaw size={36} style={{ color: theme.primary, marginBottom: "1rem" }} />
            <h3 
              style={{ 
                fontSize: "clamp(1.4rem, 3vw, 1.75rem)", 
                fontWeight: "700", 
                marginBottom: "0.75rem",
                color: theme.text
              }}
            >
              Migration Timing & Strategy
            </h3>
            <p style={{ color: theme.textMuted, fontSize: "0.95rem" }}>How we position you for the best wildlife encounters</p>
          </div>
          <div className="row g-3 justify-content-center" style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {[
              "Real-time herd tracking via scout network & satellite data",
              "Flexible daily routing based on morning wildlife reports",
              "Strategic camp locations near known crossing points",
              "Early morning & late afternoon drives for peak predator activity",
              "Photography-focused positioning for river crossings & hunts",
              "Expert guides trained in migration behavior & animal tracking"
            ].map((item, idx) => (
              <div 
                key={idx}
                className="col-12 col-md-6 col-lg-4"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.75rem"
                }}
              >
                <IoMdCheckmarkCircle size={20} style={{ color: theme.primary, flexShrink: 0, marginTop: "2px" }} />
                <span style={{ fontSize: "0.95rem", color: theme.text, lineHeight: 1.5 }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ================= FULL ITINERARY TIMELINE ================= */}
        <section>
          <div className="text-center mb-5">
            <h2 
              style={{ 
                fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
                marginBottom: "0.5rem", 
                color: theme.text,
                fontWeight: "700"
              }}
            >
              Your 7-Day Northern Circuit Journey
            </h2>
            <p style={{ color: theme.textMuted }}>Complete itinerary from Tarangire to Ngorongoro Crater</p>
          </div>

          <div style={{ position: "relative" }}>
            {/* Timeline connector line (desktop) */}
            <div 
              className="d-none d-lg-block"
              style={{
                position: "absolute",
                left: "50%",
                top: "0",
                bottom: "0",
                width: "2px",
                background: `linear-gradient(to bottom, ${theme.primary}33, ${theme.accent}33, transparent)`,
                transform: "translateX(-50%)"
              }}
            />

            {fullItinerary.map((day, i) => (
              <article 
                key={i} 
                className={`row g-4 align-items-center mb-4 ${i % 2 === 0 ? 'flex-lg-row' : 'flex-lg-row-reverse'}`}
                style={{
                  background: theme.cardBg,
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  position: "relative",
                  zIndex: 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = "0 14px 35px rgba(0,0,0,0.12)";
                  e.currentTarget.style.borderColor = theme.primary + "66";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)";
                  e.currentTarget.style.borderColor = "rgba(0,0,0,0.04)";
                }}
              >
                {/* Day Number Badge */}
                <div 
                  className="position-absolute top-0 start-0 m-3 rounded-pill px-3 py-1"
                  style={{
                    background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`,
                    color: "#1e293b",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    zIndex: 2,
                    boxShadow: "0 4px 12px rgba(217, 119, 6, 0.4)"
                  }}
                >
                  Day {day.dayNum}
                </div>

                {/* Image Column */}
                <div className="col-lg-5" style={{ position: "relative", minHeight: "280px" }}>
                  <div
                    style={{
                      height: "100%",
                      minHeight: "280px",
                      backgroundImage: `url(${day.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transition: "transform 0.5s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.03)"}
                    onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                  />
                  {/* Gradient overlay for text readability */}
                  <div 
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(to top, rgba(23,15,8,0.75), transparent 60%)"
                    }}
                  />
                </div>

                {/* Content Column */}
                <div className="col-lg-7 p-4 p-lg-5">
                  <h3 
                    style={{ 
                      fontSize: "1.4rem", 
                      marginBottom: "1rem", 
                      fontWeight: "700",
                      color: theme.text,
                      paddingRight: "60px",
                      lineHeight: 1.3
                    }}
                  >
                    {day.title}
                  </h3>
                  
                  <p style={{ lineHeight: 1.7, color: theme.textMuted, marginBottom: "1.25rem", fontSize: "0.95rem" }}>
                    {day.body}
                  </p>
                  
                  {/* Tags */}
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
                    {day.tags.map((tag, idx) => (
                      <span 
                        key={idx}
                        style={{
                          background: `${theme.primary}10`,
                          color: theme.primary,
                          padding: "0.35rem 0.85rem",
                          borderRadius: "50px",
                          fontSize: "0.8rem",
                          fontWeight: "500",
                          border: `1px solid ${theme.primary}25`
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  {/* Meals/Accommodation Info */}
                  <div 
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.6rem 1.2rem",
                      background: `linear-gradient(135deg, ${theme.accent}15, ${theme.accent}08)`,
                      borderRadius: "10px",
                      fontSize: "0.9rem",
                      color: theme.accent,
                      fontWeight: "500",
                      border: `1px solid ${theme.accent}30`
                    }}
                  >
                    <FaUtensils size={14} />
                    {day.activities}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ================= CTA SECTION ================= */}
        <section 
          className="text-center mt-6 pt-5"
          style={{ 
            borderTop: `1px solid rgba(0,0,0,0.06)`,
            background: `linear-gradient(135deg, ${theme.primary}08, ${theme.accent}08)`,
            borderRadius: "20px",
            padding: "3rem 2rem",
            marginTop: "4rem"
          }}
        >
          <FaGlobeAfrica size={42} style={{ color: theme.primary, marginBottom: "1rem" }} />
          <h3 
            style={{ 
              fontSize: "clamp(1.5rem, 4vw, 2rem)", 
              fontWeight: "700", 
              marginBottom: "1rem",
              color: theme.text
            }}
          >
            Ready to Witness the Great Migration?
          </h3>
          <p style={{ color: theme.textMuted, maxWidth: "600px", margin: "0 auto 2rem", lineHeight: 1.6 }}>
            Book your Northern Circuit safari with migration-expert guides and handpicked camps. Limited availability for peak crossing seasons (July–October). Custom itinerary adjustments available.
          </p>
          <button 
            className="btn btn-lg px-5 py-3 rounded-pill"
            style={{ 
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`,
              border: "none",
              fontWeight: "600",
              color: "white",
              boxShadow: `0 6px 20px ${theme.primary}40`,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}50`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = `0 6px 20px ${theme.primary}40`;
            }}
            onClick={()=>appContext.navTo({item:'plan-trip'})}
          >
            Reserve Your Safari <IoMdArrowDropright size={20} />
          </button>
          <p style={{ fontSize: "0.85rem", color: theme.textMuted, marginTop: "1.5rem", margin: 0 }}>
            <FaRegClock size={14} className="me-1" /> Free migration timing consultation • Flexible booking terms
          </p>
        </section>

      </div>

      {/* ================= CSS ANIMATIONS ================= */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { transition: none !important; animation: none !important; }
        }
        @media (max-width: 991px) {
          [style*="left: 50%"] { display: none !important; }
        }
      `}</style>
    </div>
  );
}